import React, { useMemo, useState } from 'react';
import {
  Activity,
  BarChart3,
  Bot,
  ChevronLeft,
  ChevronRight,
  ClipboardCheck,
  Database,
  PlugZap,
  Home,
  CircleHelp,
  Microscope,
  Settings,
  Users,
  Sparkles,
  CreditCard,
} from 'lucide-react';
import metricusLogo from '@process-advisor/assets/metricusLogoSmall.png';

const WORKSPACE_ITEMS = [
  ['overview', 'Home', Home],
  ['models', 'Models', Database],
  ['connections', 'Connections', PlugZap],
];

const MODEL_ITEMS = [
  ['evidence', 'Evidence', BarChart3],
  ['explorer', 'Explorer', Microscope],
  ['advisor', 'Advisor', Bot],
  ['value', 'Value', Sparkles],
  ['assessment', 'Assessment', ClipboardCheck],
];

const TITLES = {
  overview: 'Home',
  models: 'Models',
  connections: 'Connections',
  new: 'New model',
  billing: 'Access & Billing',
  settings: 'Settings',
  workspace: 'Workspace Management',
  advisor: 'Process Advisor',
};


function modelAccessLabel(model) {
  const source = String(
    model?.currentAccess?.source
    || model?.accessSource
    || model?.summary?.accessSource
    || model?.source?.accessSource
    || model?.entitlement?.source
    || model?.latestRuntime?.canonicalDataset?.metadata?.ingestion?.accessSource
    || ''
  ).toLowerCase();

  if (['subscription','instance_subscription','partner_client_slot'].includes(source)) {
    return { label: 'Subscription', tone: 'ok', title: 'This model is covered by a source subscription' };
  }
  if (['assessment','assessment_credit','assessment-credits','credits'].includes(source)) {
    const expiresAt = model?.currentAccess?.expiresAt || model?.entitlement?.expiresAt || null;
    const remaining = expiresAt
      ? Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 86400000))
      : null;
    return {
      label: remaining == null ? 'Assessment' : `Assessment · ${remaining}d`,
      tone: 'ok',
      title: expiresAt ? `Assessment active until ${new Date(expiresAt).toLocaleDateString()}` : 'Assessment active for this model',
    };
  }
  if (['free','free_limited','free_assessment','billing_parked','included','standard'].includes(source)) {
    return { label: 'Free allowance', tone: 'free', title: 'This model is using the free processing allowance' };
  }
  return null;
}

function NavButton({ id, label, Icon, active, collapsed, onClick }) {
  return (
    <button
      type="button"
      className={`saas-nav-item ${active ? 'active' : ''}`}
      onClick={onClick}
      title={collapsed ? label : undefined}
      aria-label={label}
    >
      <Icon size={18}/>
      {!collapsed && <span>{label}</span>}
    </button>
  );
}

export default function SaasShell({
  route,
  onRoute,
  children,
  activeModel,
  advisorSection = 'evidence',
  onAdvisorSection,
  onCloseModel,
  identity,
  onSignOut,
  accessStatus,
}) {
  const [collapsed, setCollapsed] = useState(false);
  const toggleCollapsed = () => setCollapsed(prev => !prev);

  const modelSubtitle = activeModel?.name || '';
  const breadcrumb = useMemo(() => {
    if (route === 'advisor' && activeModel) {
      const sectionLabel = MODEL_ITEMS.find(([id]) => id === advisorSection)?.[1] || 'Process Advisor';
      return ['Models', activeModel.name, sectionLabel];
    }
    return ['Process Advisor', TITLES[route] || 'Model'];
  }, [route, activeModel, advisorSection]);

  const modelOpen = route === 'advisor' && !!activeModel;
  const activeModelAccess = modelOpen ? modelAccessLabel(activeModel) : null;
  const workspaceAccess = accessStatus ? {
    label: accessStatus?.subscription?.active
      ? (accessStatus.subscription.type==='individual'?'Individual':'Workspace')
      : 'Free trial',
    tone: accessStatus?.subscription?.active?'ok':'free',
    title: 'Access & Billing',
  } : null;
  const topbarAccess = activeModelAccess || workspaceAccess;

  return (
    <div className={`saas-root ${collapsed ? 'sidebar-collapsed' : ''}`}>
      <aside className="saas-sidebar">
        <div className="saas-brand">
          <img className="saas-brand-logo" src={metricusLogo} alt="Metricus"/>
          {!collapsed && <div><div className="saas-brand-title">Process Advisor</div><div className="saas-brand-sub">Model workspace</div></div>}
        </div>

        {!collapsed && <div className="saas-nav-label">Workspace</div>}
        <nav className="saas-nav">
          {WORKSPACE_ITEMS.map(([id,label,Icon]) => (
            <NavButton
              key={id}
              id={id}
              label={label}
              Icon={Icon}
              collapsed={collapsed}
              active={!modelOpen && route === id}
              onClick={() => onRoute(id)}
            />
          ))}
        </nav>

        {activeModel && <>
          <div className="saas-nav-divider"/>
          {!collapsed && <div className="saas-nav-context"><span>Current model</span><b title={activeModel.name}>{activeModel.name}</b></div>}
          {!collapsed && <div className="saas-nav-label">Process Advisor</div>}
          <nav className="saas-nav saas-model-nav">
            {MODEL_ITEMS.map(([id,label,Icon]) => (
              <NavButton
                key={id}
                id={id}
                label={label}
                Icon={Icon}
                collapsed={collapsed}
                active={modelOpen && advisorSection === id}
                onClick={() => onAdvisorSection?.(id)}
              />
            ))}
          </nav>
        </>}

        <div className="saas-nav-divider push-bottom"/>
        <nav className="saas-nav saas-nav-bottom">
          <NavButton
            id="billing"
            label="Access & Billing"
            Icon={CreditCard}
            collapsed={collapsed}
            active={!modelOpen && route === 'billing'}
            onClick={() => onRoute('billing')}
          />
          <NavButton id="workspace" label="Workspace Management" Icon={Users} collapsed={collapsed} active={!modelOpen && route === 'workspace'} onClick={() => onRoute('workspace')}/>
          <NavButton
            id="settings"
            label="Settings"
            Icon={Settings}
            collapsed={collapsed}
            active={!modelOpen && route === 'settings'}
            onClick={() => onRoute('settings')}
          />
          <a
            className="saas-nav-item"
            href="https://metricus.atlassian.net/wiki/spaces/POJ/overview"
            target="_blank"
            rel="noopener noreferrer"
            title={collapsed ? 'Help — opens in new tab' : undefined}
            aria-label="Help — opens in new tab"
          >
            <CircleHelp size={18}/>
            {!collapsed && <span>Help</span>}
          </a>
          <button type="button" className="saas-nav-item sidebar-toggle icon-only" onClick={toggleCollapsed} title={collapsed ? 'Expand navigation' : 'Collapse navigation'} aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}>
            {collapsed ? <ChevronRight size={18}/> : <ChevronLeft size={18}/>}
          </button>
        </nav>

      </aside>

      <div className="saas-content">
        <header className="saas-topbar">
          <div className="saas-breadcrumb">
            {breadcrumb.map((part, index) => <React.Fragment key={`${part}-${index}`}>
              {index > 0 && <span className="saas-breadcrumb-sep">/</span>}
              {index === breadcrumb.length - 1 ? <b>{part}</b> : <span>{part}</span>}
            </React.Fragment>)}
          </div>
          <div className="saas-topbar-actions">
            {topbarAccess && <button
              type="button"
              className={`access-topbar-pill ${topbarAccess.tone}`}
              onClick={()=>onRoute('billing')}
              title={topbarAccess.title || 'Access & Billing'}
            >
              <CreditCard size={15}/>
              <span>{topbarAccess.label}</span>
            </button>}
            {modelOpen && <button type="button" className="pa-secondary compact model-back-button" onClick={onCloseModel}><Database size={14}/> Models</button>}
            {identity && <details className="account-menu">
              <summary><span className="account-avatar">{(identity.user?.displayName||identity.user?.email||'U').slice(0,1).toUpperCase()}</span><span className="account-label"><b>{identity.user?.displayName||identity.user?.email}</b><small>{identity.workspace?.name || 'Workspace'}</small></span></summary>
              <div className="account-popover">
                <div className="account-meta"><b>{identity.user?.displayName||identity.user?.email}</b><span>{identity.user?.email}</span><span>{identity.workspace?.name || 'Workspace'}</span></div>
                <button type="button" onClick={()=>onRoute('billing')}>Access &amp; Billing</button>
                <button type="button" onClick={()=>onRoute('workspace')}>Workspace Management</button>
                <button type="button" onClick={()=>onRoute('settings')}>Settings</button>
                <button type="button" onClick={onSignOut}>Sign out</button>
              </div>
            </details>}
          </div>
        </header>
        <main className={`saas-main ${modelOpen ? 'saas-main-advisor' : ''} ${route === 'overview' ? 'saas-main-home' : ''}`}>{children}</main>
      </div>
    </div>
  );
}
